/**
 * Fatigue Alerts Management Page
 * Monitor and manage crew fatigue risk alerts
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  AlertTriangle, Bell, CheckCircle, Clock, Settings, RefreshCw,
  User, Activity, Moon, Heart, Brain, Shield, AlertCircle, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Severity colors
const SEVERITY_COLORS = {
  low: 'bg-blue-500',
  medium: 'bg-yellow-500',
  high: 'bg-orange-500',
  critical: 'bg-red-500'
};

const SEVERITY_TEXT = {
  low: 'text-blue-400',
  medium: 'text-yellow-400',
  high: 'text-orange-400',
  critical: 'text-red-400'
};

const STATUS_COLORS = {
  active: 'bg-red-500/20 text-red-400 border-red-500',
  acknowledged: 'bg-yellow-500/20 text-yellow-400 border-yellow-500',
  resolved: 'bg-green-500/20 text-green-400 border-green-500',
  escalated: 'bg-purple-500/20 text-purple-400 border-purple-500'
};

export default function FatigueAlertsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('alerts');
  const [alerts, setAlerts] = useState([]);
  const [myAlerts, setMyAlerts] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  const token = localStorage.getItem('token');
  const userRole = JSON.parse(localStorage.getItem('user') || '{}').role;
  const isManager = ['admin', 'safety_manager', 'quality_manager'].includes(userRole);

  useEffect(() => {
    fetchAlerts();
    fetchMyAlerts();
    if (isManager) {
      fetchConfig();
    }
  }, [filterStatus, filterSeverity]);

  const fetchAlerts = async () => {
    try {
      let url = `${API_URL}/api/gamification/fatigue-alerts?limit=50`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      if (filterSeverity !== 'all') url += `&severity=${filterSeverity}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gamification/fatigue-alerts/my?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch my alerts:', error);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gamification/fatigue-alerts/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/gamification/fatigue-alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        fetchAlerts();
        fetchMyAlerts();
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleResolve = async () => {
    if (!selectedAlert || !resolutionNotes.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/gamification/fatigue-alerts/${selectedAlert.id}/resolve?resolution_notes=${encodeURIComponent(resolutionNotes)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Alert resolved');
        setShowResolveModal(false);
        setSelectedAlert(null);
        setResolutionNotes('');
        fetchAlerts();
        fetchMyAlerts();
      }
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const handleUpdateConfig = async () => {
    try {
      const params = new URLSearchParams({
        enabled: config.enabled,
        threshold_low: config.thresholds.low,
        threshold_medium: config.thresholds.medium,
        threshold_high: config.thresholds.high,
        threshold_critical: config.thresholds.critical,
        auto_notify_supervisor: config.auto_notify_supervisor,
        cooldown_hours: config.cooldown_hours
      });
      
      const res = await fetch(`${API_URL}/api/gamification/fatigue-alerts/config?${params}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Configuration updated');
      }
    } catch (error) {
      toast.error('Failed to update configuration');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Count stats
  const activeCount = alerts.filter(a => a.status === 'active').length;
  const acknowledgedCount = alerts.filter(a => a.status === 'acknowledged').length;
  const criticalCount = alerts.filter(a => a.severity === 'critical' && a.status === 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="fatigue-alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bell className="h-6 w-6 text-red-500" />
            {t('frms.alerts')}
          </h1>
          <p className="text-gray-400 mt-1">
            {t('frms.subtitle')}
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchAlerts(); fetchMyAlerts(); }}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={criticalCount > 0 ? 'border-red-500 bg-red-950/20' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">{t('common.critical')} {t('frms.alerts')}</p>
                <p className={`text-3xl font-bold ${criticalCount > 0 ? 'text-red-500' : 'text-white'}`}>
                  {criticalCount}
                </p>
              </div>
              <AlertTriangle className={`h-10 w-10 ${criticalCount > 0 ? 'text-red-500 animate-pulse' : 'text-gray-600'}`} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">{t('common.active')} {t('frms.alerts')}</p>
                <p className="text-3xl font-bold text-orange-500">{activeCount}</p>
              </div>
              <AlertCircle className="h-10 w-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">{t('common.pending')}</p>
                <p className="text-3xl font-bold text-yellow-500">{acknowledgedCount}</p>
              </div>
              <Eye className="h-10 w-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">{t('frms.alerts')}</p>
                <p className="text-3xl font-bold text-sky-500">{myAlerts.filter(a => a.status === 'active').length}</p>
              </div>
              <User className="h-10 w-10 text-sky-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="alerts" data-testid="tab-all-alerts">
            All Alerts
          </TabsTrigger>
          <TabsTrigger value="my-alerts" data-testid="tab-my-alerts">
            My Alerts
          </TabsTrigger>
          {isManager && (
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          )}
        </TabsList>

        {/* All Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Alerts List */}
          <ScrollArea className="h-[500px]">
            {alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <Card key={alert.id} className={`border-l-4 ${
                    alert.severity === 'critical' ? 'border-l-red-500' :
                    alert.severity === 'high' ? 'border-l-orange-500' :
                    alert.severity === 'medium' ? 'border-l-yellow-500' : 'border-l-blue-500'
                  }`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-lg ${SEVERITY_COLORS[alert.severity]} flex items-center justify-center`}>
                            <AlertTriangle className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white font-semibold">{alert.user_name}</span>
                              <Badge className={STATUS_COLORS[alert.status]}>
                                {alert.status}
                              </Badge>
                              <Badge className={`${SEVERITY_COLORS[alert.severity]} text-white`}>
                                {alert.severity}
                              </Badge>
                            </div>
                            <p className="text-gray-400 text-sm mb-2">{alert.recommendation}</p>
                            
                            {/* Risk Factors */}
                            {alert.risk_factors?.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {alert.risk_factors.map((rf, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {rf.factor}: {rf.value}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Activity className="h-3 w-3" />
                                Score: {alert.fatigue_score}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(alert.created_at)}
                              </span>
                              {alert.supervisor_notified && (
                                <span className="flex items-center gap-1 text-green-500">
                                  <Bell className="h-3 w-3" />
                                  Supervisor notified
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          {alert.status === 'active' && (
                            <Button size="sm" variant="outline" onClick={() => handleAcknowledge(alert.id)}>
                              Acknowledge
                            </Button>
                          )}
                          {alert.status !== 'resolved' && isManager && (
                            <Button size="sm" onClick={() => { setSelectedAlert(alert); setShowResolveModal(true); }}>
                              Resolve
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {alert.resolution_notes && (
                        <div className="mt-3 p-3 bg-green-900/20 rounded-lg border border-green-800">
                          <p className="text-sm text-green-400">
                            <strong>Resolution:</strong> {alert.resolution_notes}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Resolved by {alert.resolved_by} on {formatDate(alert.resolved_at)}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500 opacity-50" />
                <p className="text-lg">No fatigue alerts</p>
                <p className="text-sm">All crew members are within safe fatigue levels</p>
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* My Alerts Tab */}
        <TabsContent value="my-alerts">
          <Card>
            <CardHeader>
              <CardTitle>My Fatigue Alerts</CardTitle>
              <CardDescription>Alerts related to your biometric data</CardDescription>
            </CardHeader>
            <CardContent>
              {myAlerts.length > 0 ? (
                <div className="space-y-3">
                  {myAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={`p-4 rounded-lg border ${
                        alert.status === 'active' ? 'bg-red-950/20 border-red-800' :
                        alert.status === 'acknowledged' ? 'bg-yellow-950/20 border-yellow-800' :
                        'bg-green-950/20 border-green-800'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={SEVERITY_COLORS[alert.severity]}>{alert.severity}</Badge>
                          <Badge className={STATUS_COLORS[alert.status]}>{alert.status}</Badge>
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(alert.created_at)}</span>
                      </div>
                      <p className="text-white mb-2">{alert.recommendation}</p>
                      <p className="text-sm text-gray-400">Fatigue Score: {alert.fatigue_score}</p>
                      
                      {alert.status === 'active' && (
                        <Button size="sm" className="mt-3" onClick={() => handleAcknowledge(alert.id)}>
                          I Acknowledge This Alert
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p>No fatigue alerts for you</p>
                  <p className="text-sm">Keep maintaining healthy fatigue levels!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab (Admin only) */}
        {isManager && (
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Alert Configuration</CardTitle>
                <CardDescription>Configure fatigue alert thresholds and notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {config && (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Enable Fatigue Alerts</Label>
                        <p className="text-sm text-gray-500">Automatically generate alerts based on biometric data</p>
                      </div>
                      <Switch
                        checked={config.enabled}
                        onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label>Low Threshold</Label>
                        <Input
                          type="number"
                          value={config.thresholds?.low || 60}
                          onChange={(e) => setConfig({
                            ...config,
                            thresholds: { ...config.thresholds, low: parseInt(e.target.value) }
                          })}
                        />
                        <p className="text-xs text-gray-500 mt-1">Score below triggers LOW alert</p>
                      </div>
                      <div>
                        <Label>Medium Threshold</Label>
                        <Input
                          type="number"
                          value={config.thresholds?.medium || 45}
                          onChange={(e) => setConfig({
                            ...config,
                            thresholds: { ...config.thresholds, medium: parseInt(e.target.value) }
                          })}
                        />
                        <p className="text-xs text-gray-500 mt-1">Score below triggers MEDIUM alert</p>
                      </div>
                      <div>
                        <Label>High Threshold</Label>
                        <Input
                          type="number"
                          value={config.thresholds?.high || 30}
                          onChange={(e) => setConfig({
                            ...config,
                            thresholds: { ...config.thresholds, high: parseInt(e.target.value) }
                          })}
                        />
                        <p className="text-xs text-gray-500 mt-1">Score below triggers HIGH alert</p>
                      </div>
                      <div>
                        <Label>Critical Threshold</Label>
                        <Input
                          type="number"
                          value={config.thresholds?.critical || 20}
                          onChange={(e) => setConfig({
                            ...config,
                            thresholds: { ...config.thresholds, critical: parseInt(e.target.value) }
                          })}
                        />
                        <p className="text-xs text-gray-500 mt-1">Score below triggers CRITICAL alert</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Auto-Notify Supervisors</Label>
                        <p className="text-sm text-gray-500">Automatically notify supervisors for high/critical alerts</p>
                      </div>
                      <Switch
                        checked={config.auto_notify_supervisor}
                        onCheckedChange={(v) => setConfig({ ...config, auto_notify_supervisor: v })}
                      />
                    </div>

                    <div>
                      <Label>Cooldown Period (hours)</Label>
                      <Input
                        type="number"
                        className="w-32"
                        value={config.cooldown_hours || 8}
                        onChange={(e) => setConfig({ ...config, cooldown_hours: parseInt(e.target.value) })}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Don't create duplicate alerts for the same user within this period
                      </p>
                    </div>

                    <Button onClick={handleUpdateConfig}>Save Configuration</Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Resolve Modal */}
      <Dialog open={showResolveModal} onOpenChange={setShowResolveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Fatigue Alert</DialogTitle>
            <DialogDescription>
              Provide resolution notes for this alert
            </DialogDescription>
          </DialogHeader>
          
          {selectedAlert && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="text-white font-medium">{selectedAlert.user_name}</p>
                <p className="text-sm text-gray-400">{selectedAlert.recommendation}</p>
              </div>
              
              <div>
                <Label>Resolution Notes *</Label>
                <Textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe the actions taken to address this fatigue risk..."
                  rows={4}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveModal(false)}>Cancel</Button>
            <Button onClick={handleResolve} disabled={!resolutionNotes.trim()}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Resolve Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
